import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form } from 'react-bootstrap';
import accountApi from '../services/accountApi';
import bakeonatorLogo from '../assets/images/logos/bakeonator_logo.png';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const missingParams = useMemo(() => !uid || !token, [uid, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await accountApi.confirmPasswordReset({ uid, token, password });
      setSuccess(true);
    } catch (err) {
      if (err.code === 'invalid_token') {
        setError('This password-reset link is invalid or has expired.');
      } else if (err.code === 'invalid_password') {
        setError(err.message || 'Password does not meet requirements.');
      } else {
        setError(err.message || 'Unable to reset password');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div className="card shadow">
            <div className="card-body text-center">
              <img
                src={bakeonatorLogo}
                alt="Bakeonator Logo"
                width="auto"
                height="100"
                className="mx-auto d-block mb-3"
              />
              <h2 className="card-title mb-4">Reset password</h2>

              {missingParams && (
                <Alert variant="danger">
                  This reset link is incomplete. Request a new link from the forgot password page.
                </Alert>
              )}

              {success ? (
                <>
                  <Alert variant="success">Password has been reset. You can sign in.</Alert>
                  <Link to="/login" className="btn btn-primary">
                    Sign in
                  </Link>
                </>
              ) : (
                <>
                  {error && <Alert variant="danger">{error}</Alert>}
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3 text-start">
                      <Form.Label htmlFor="password">New password</Form.Label>
                      <Form.Control
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={missingParams}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3 text-start">
                      <Form.Label htmlFor="confirmPassword">Confirm password</Form.Label>
                      <Form.Control
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={missingParams}
                      />
                    </Form.Group>
                    <div className="d-grid mb-3">
                      <Button type="submit" disabled={submitting || missingParams}>
                        {submitting ? 'Saving…' : 'Reset password'}
                      </Button>
                    </div>
                  </Form>
                  <Link to="/forgot-password" className="small me-3">
                    Request new link
                  </Link>
                  <Link to="/login" className="small">
                    Back to sign in
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
